import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ComponentAccess } from '../../../common/decorators/component-access.decorator';

// The SVG animation is a single 3s once-off pass (fill-mode: forwards) —
// hold exactly that long, then fade the overlay.
const ANIMATION_DURATION_MS = 3000;
// If the SVG renders but `animationstart` never fires (e.g. prefers-reduced-
// motion), start the dismiss countdown anyway this long after the SVG is set.
const RENDER_GRACE_MS = 1500;
const MAX_TOTAL_MS = 7000; // absolute upper bound — splash always dismisses
const FADE_OUT_MS = 350;
const SVG_BASE_PATH = 'assets/splash';
const FALLBACK_PNG_LIGHT = 'assets/splash/splash-light.png';
const FALLBACK_PNG_DARK = 'assets/splash/splash-dark.png';

@Component({
  selector: 'app-sequal-splash',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (isBrowser) {
      <div
        class="splash-overlay"
        [class.dark]="isDark()"
        [class.fade-out]="fadingOut()"
        role="img"
        aria-label="Sequal loading"
      >
        @if (svgHtml(); as html) {
          <div
            class="splash-svg"
            [innerHTML]="html"
            (animationstart)="onAnimationStarted()"
          ></div>
        } @else {
          <img class="splash-svg" [src]="fallbackSrc()" alt="" />
        }
      </div>
    }
  `,
  styles: [
    `
      :host {
        display: contents;
      }

      .splash-overlay {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: #ffffff;
        opacity: 1;
        transition: opacity 350ms ease-out;
      }

      .splash-overlay.dark {
        background: #0a0a0a;
      }

      .splash-overlay.fade-out {
        opacity: 0;
      }

      .splash-svg {
        width: min(80vw, 80vh, 480px);
        height: auto;
        pointer-events: none;
      }

      .splash-svg ::ng-deep svg {
        width: 100%;
        height: 100%;
        display: block;
      }
    `,
  ],
})
@ComponentAccess('SequalSplashComponent')
export class SequalSplashComponent implements OnInit {
  readonly holdLoops = input<number>(1);

  readonly animationComplete = output<void>();

  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sanitizer = inject(DomSanitizer);
  protected readonly isBrowser = isPlatformBrowser(this.platformId);

  protected readonly isDark = signal<boolean>(false);
  protected readonly fadingOut = signal<boolean>(false);
  protected readonly svgHtml = signal<SafeHtml | null>(null);
  protected readonly fallbackSrc = signal<string>(FALLBACK_PNG_LIGHT);

  private mediaQuery: MediaQueryList | undefined;
  private mediaListener: ((e: MediaQueryListEvent) => void) | undefined;
  private fadeTimer: number | undefined;
  private dismissTimer: number | undefined;
  private maxTimer: number | undefined;
  private renderGraceTimer: number | undefined;
  private timerStarted = false;
  private completed = false;

  ngOnInit(): void {
    if (!this.isBrowser) {
      return;
    }

    try {
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      this.applyTheme(this.mediaQuery.matches);
      this.mediaListener = (e: MediaQueryListEvent) =>
        this.applyTheme(e.matches);
      if (typeof this.mediaQuery.addEventListener === 'function') {
        this.mediaQuery.addEventListener('change', this.mediaListener);
      } else if (
        typeof (this.mediaQuery as unknown as { addListener?: unknown })
          .addListener === 'function'
      ) {
        (
          this.mediaQuery as unknown as {
            addListener: (l: (e: MediaQueryListEvent) => void) => void;
          }
        ).addListener(this.mediaListener);
      }
    } catch {
      // matchMedia unsupported / restricted — light splash is fine.
      this.applyTheme(false);
    }

    // Safety upper bound: even if SVG fetch never resolves, dismiss.
    this.maxTimer = window.setTimeout(() => this.complete(), MAX_TOTAL_MS);

    this.destroyRef.onDestroy(() => this.cleanup());
  }

  private applyTheme(dark: boolean): void {
    this.isDark.set(dark);
    this.fallbackSrc.set(dark ? FALLBACK_PNG_DARK : FALLBACK_PNG_LIGHT);
    void this.loadSvg(dark);
  }

  private async loadSvg(dark: boolean): Promise<void> {
    const file = dark ? 'splash-dark.svg' : 'splash-light.svg';
    try {
      const res = await fetch(`${SVG_BASE_PATH}/${file}`);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const text = await res.text();
      this.svgHtml.set(this.sanitizer.bypassSecurityTrustHtml(text));
      // Don't start the dismiss countdown on fetch-complete — on slower
      // devices the SVG still needs ~1-2s to parse/render before the
      // animation visibly starts, which would cut it short. Wait for
      // animationstart (see onAnimationStarted), with a grace fallback in
      // case the animation never fires (e.g. reduced-motion).
      this.renderGraceTimer = window.setTimeout(
        () => this.startDismissTimer(),
        RENDER_GRACE_MS,
      );
    } catch {
      // SVG load failed — show the static <img> fallback; no animation to
      // wait for, so begin the dismiss countdown immediately.
      this.svgHtml.set(null);
      this.startDismissTimer();
    }
  }

  // Fired when the SVG's internal CSS animation actually begins — the correct
  // moment to start the hold countdown so the full animation is visible regardless
  // of how long the device took to render the SVG.
  protected onAnimationStarted(): void {
    if (this.renderGraceTimer) {
      window.clearTimeout(this.renderGraceTimer);
      this.renderGraceTimer = undefined;
    }
    this.startDismissTimer();
  }

  private startDismissTimer(): void {
    if (this.timerStarted) {
      return;
    }
    this.timerStarted = true;
    const holdMs = ANIMATION_DURATION_MS * Math.max(1, this.holdLoops());
    this.fadeTimer = window.setTimeout(() => this.fadingOut.set(true), holdMs);
    this.dismissTimer = window.setTimeout(
      () => this.complete(),
      holdMs + FADE_OUT_MS,
    );
  }

  private complete(): void {
    if (this.completed) {
      return;
    }
    this.completed = true;
    this.animationComplete.emit();
  }

  private cleanup(): void {
    if (this.fadeTimer) window.clearTimeout(this.fadeTimer);
    if (this.dismissTimer) window.clearTimeout(this.dismissTimer);
    if (this.maxTimer) window.clearTimeout(this.maxTimer);
    if (this.renderGraceTimer) window.clearTimeout(this.renderGraceTimer);
    if (this.mediaQuery && this.mediaListener) {
      if (typeof this.mediaQuery.removeEventListener === 'function') {
        this.mediaQuery.removeEventListener('change', this.mediaListener);
      } else if (
        typeof (this.mediaQuery as unknown as { removeListener?: unknown })
          .removeListener === 'function'
      ) {
        (
          this.mediaQuery as unknown as {
            removeListener: (l: (e: MediaQueryListEvent) => void) => void;
          }
        ).removeListener(this.mediaListener);
      }
    }
  }
}
